import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import type { Seller, User } from '@sdr-agend/admin-schemas'

// Story 12.8 (Epic E12) — 'sdr' follows the exact same branch as any other
// non-vendedor role below (just `name`, no `sellerId` section).
export type UserRole = 'admin' | 'manager' | 'operator' | 'viewer' | 'vendedor' | 'sdr'

export interface UserFormValues {
  email: string
  name: string
  role: UserRole
  active: boolean
  password: string
  /**
   * 2026-07-21 (user request) — set only when `role === 'vendedor'`. The
   * backend derives name/phone/ghl_user_id from this seller instead of the
   * admin retyping data that's already on file in `sellers`.
   */
  sellerId?: string
}

interface UserFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Quando `null` o dialog opera em modo "create". */
  user: User | null
  onSubmit: (values: UserFormValues) => Promise<void> | void
  submitting: boolean
  /**
   * Vendedores disponíveis pra linkar um login `role="vendedor"`. Passado
   * pela página pai (`Users.tsx`), que já busca `/admin/sellers`.
   */
  sellers: ReadonlyArray<Seller>
}

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin (acesso total + gerencia usuários)',
  manager: 'Manager (operação + relatórios)',
  operator: 'Operator (operação diária)',
  viewer: 'Viewer (somente leitura)',
  vendedor: 'Vendedor (vê só as próprias reuniões/conversas)',
  sdr: 'SDR (vê e atua só na própria carteira de leads)',
}

const ROLES: UserRole[] = ['admin', 'manager', 'operator', 'viewer', 'vendedor', 'sdr']

const EMPTY: UserFormValues = {
  email: '',
  name: '',
  role: 'operator',
  active: true,
  password: '',
  sellerId: undefined,
}

export function UserFormDialog({
  open,
  onOpenChange,
  user,
  onSubmit,
  submitting,
  sellers,
}: UserFormDialogProps): React.ReactElement {
  const isEdit = user !== null
  const [values, setValues] = useState<UserFormValues>(EMPTY)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setError(null)
      if (user) {
        setValues({
          email: user.email,
          name: user.name,
          role: user.role,
          active: user.active,
          password: '',
          sellerId: undefined,
        })
      } else {
        setValues(EMPTY)
      }
    }
  }, [open, user])

  const isVendedor = values.role === 'vendedor'
  const sellersWithGhlId = sellers.filter((s) => s.ghlUserId !== undefined && s.ghlUserId !== '')

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setError(null)
    try {
      await onSubmit(values)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar usuário.'
      setError(message)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar usuário' : 'Novo usuário'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Atualize nome, role, status e (opcionalmente) senha. Email não pode ser alterado.'
              : 'Crie um novo usuário do painel. A senha deve ter ao menos 12 caracteres, com maiúscula, minúscula, número e caractere especial.'}
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-1.5">
            <Label htmlFor="user-email">Email</Label>
            <Input
              id="user-email"
              type="email"
              autoComplete="off"
              required
              disabled={isEdit || submitting}
              value={values.email}
              onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
              placeholder="usuario@reinoeducacao.com"
            />
            {isEdit && (
              <p className="text-xs text-muted-foreground">
                Para alterar email, use SQL com trilha de auditoria manual (Story 1.7 §AC #7).
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="user-name">Nome</Label>
            <Input
              id="user-name"
              type="text"
              required={!isVendedor}
              disabled={submitting || isVendedor}
              value={values.name}
              onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
              placeholder="Nome completo"
            />
            {isVendedor && (
              <p className="text-xs text-muted-foreground">
                Preenchido automaticamente a partir do vendedor selecionado abaixo.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="user-role">Role</Label>
            <Select
              id="user-role"
              disabled={submitting}
              value={values.role}
              onChange={(e) => {
                const role = e.target.value as UserRole
                setValues((v) => ({
                  ...v,
                  role,
                  // Sai do modo vendedor -> descarta o link (nome volta pra edição manual).
                  sellerId: role === 'vendedor' ? v.sellerId : undefined,
                }))
              }}
            >
              {/* Trocar PARA vendedor só é suportado na criação — editar um usuário
                  existente pra vendedor exigiria linkar um seller, que o endpoint
                  PATCH ainda não aceita. Se o usuário já É vendedor, mantemos a
                  opção pra não quebrar a edição dos outros campos dele. */}
              {ROLES.filter((r) => r !== 'vendedor' || !isEdit || user?.role === 'vendedor').map(
                (r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ),
              )}
            </Select>
          </div>

          {isVendedor && !isEdit && (
            <div className="space-y-1.5">
              <Label htmlFor="user-seller">Vendedor</Label>
              <Select
                id="user-seller"
                required
                disabled={submitting}
                value={values.sellerId ?? ''}
                onChange={(e) => {
                  const sellerId = e.target.value === '' ? undefined : e.target.value
                  const seller = sellersWithGhlId.find((s) => s.id === sellerId)
                  setValues((v) => ({ ...v, sellerId, name: seller?.name ?? '' }))
                }}
              >
                <option value="">— selecione —</option>
                {sellersWithGhlId.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-muted-foreground">
                Só vendedores com <code>ghl_user_id</code> cadastrado aparecem aqui — sem isso não
                dá pra linkar o login.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="user-password">
              {isEdit ? 'Nova senha (opcional)' : 'Senha'}
            </Label>
            <Input
              id="user-password"
              type="password"
              autoComplete="new-password"
              required={!isEdit}
              disabled={submitting}
              value={values.password}
              onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))}
              placeholder={isEdit ? 'Deixe em branco para manter' : '••••••••••••'}
              minLength={isEdit && values.password === '' ? 0 : 12}
            />
            <p className="text-xs text-muted-foreground">
              Mín. 12 caracteres, com maiúscula, minúscula, número e especial (!@#$%…).
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="user-active"
              type="checkbox"
              className="h-4 w-4 rounded border-input"
              checked={values.active}
              disabled={submitting}
              onChange={(e) => setValues((v) => ({ ...v, active: e.target.checked }))}
            />
            <Label htmlFor="user-active" className="cursor-pointer">
              Ativo (pode fazer login)
            </Label>
          </div>

          {error !== null && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {isEdit ? 'Salvar alterações' : 'Criar usuário'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
